import { apiClient } from "~/services/api-client";
import type {
  ScheduledOrder,
  ScheduledOrdersPagination,
  ScheduledOrdersApiResponse,
  ScheduledWindowBucket,
} from "./types";
import { ApiError, isAxiosError } from "~/shared/utils/api-error";

export type { ScheduledOrdersPagination };

export type ScheduledOrderFilterParams = {
  cities?: string[];
  locationCodes?: string[];
};

function buildScheduledOrderParams(
  page: number,
  limit: number,
  search?: string,
  filters?: ScheduledOrderFilterParams
): Record<string, string | number> {
  const params: Record<string, string | number> = { page, limit };
  if (search) params.search = search;
  if (filters?.cities?.length) params.cities = filters.cities.join(',');
  if (filters?.locationCodes?.length) params.locationCodes = filters.locationCodes.join(',');
  return params;
}

/**
 * Fetch a paginated page of scheduled orders.
 * The API returns pagination fields (total, page, limit) at the response root.
 */
export async function fetchScheduledOrders(
  page = 1,
  limit = 10,
  search?: string,
  filters?: ScheduledOrderFilterParams
): Promise<{ orders: ScheduledOrder[]; pagination: ScheduledOrdersPagination; contextualCities: string[] }> {
  try {
    const response = await apiClient.get<ScheduledOrdersApiResponse>(
      "/scheduled-orders",
      { params: buildScheduledOrderParams(page, limit, search, filters) }
    );

    const okStatus = response.status === 200 || response.status === 304;

    if (!response.data) {
      if (okStatus) {
        return {
          orders: [],
          pagination: { total: 0, page, limit, totalPages: 0 },
          contextualCities: [],
        };
      }
      throw new ApiError("Empty response from API", response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? "API request was not successful",
        response.status,
        response.data
      );
    }

    const orders = response.data.data ?? [];
    // Prefer the nested `pagination` object; fall back to flat root fields for
    // older API versions, and finally to derived values.
    const pag = response.data.pagination;
    const total = pag?.total ?? response.data.total ?? orders.length;
    const responsePage = pag?.page ?? response.data.page ?? page;
    const responseLimit = pag?.limit ?? response.data.limit ?? limit;
    const totalPages = pag?.totalPages ?? (responseLimit > 0 ? Math.ceil(total / responseLimit) : 0);

    return {
      orders,
      pagination: { total, page: responsePage, limit: responseLimit, totalPages },
      contextualCities: response.data.filtersContextual?.cities ?? [],
    };
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message =
        responseData?.message ?? error.message ?? "Failed to fetch scheduled orders from API";
      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) throw error;

    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}

export type UpdateScheduledOrderRequest = {
  outcome?: string;
  confirmedDeliveryDate?: string; // ISO 8601 date string
  confirmedWindow?: {
    start: number;
    end: number;
    label?: string;
    startMinutes?: number;
    endMinutes?: number;
  };
  comments?: string;
  notes?: string;
};

export type UpdateScheduledOrderResponse = {
  success: boolean;
  message?: string;
  data: ScheduledOrder;
  txId?: string;
};

/**
 * Update a scheduled order by orderId
 */
export async function updateScheduledOrder(
  orderId: string,
  data: UpdateScheduledOrderRequest
): Promise<ScheduledOrder> {
  try {
    const response = await apiClient.patch<UpdateScheduledOrderResponse>(
      `/scheduled-orders/${orderId}`,
      data
    );

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? "Failed to update scheduled order",
        response.status,
        response.data
      );
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as
        | { message?: string }
        | undefined;
      const message =
        responseData?.message ??
        error.message ??
        "Failed to update scheduled order";

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}
